import React, { useState, useEffect, useRef } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import './animatedCarousel.css';
import LazyImage from '../../LazyImage.jsx';

const effects = ['sliceTopDown', 'sliceLeftRight', 'sliceDiagonal'];
const GRID_ROWS = 4;
const GRID_COLS = 6;
const AUTO_SLIDE_DELAY = 8000;
const ANIMATION_DURATION = 1000;

const AnimatedCarousel = (props) => {
  const { title, url1, url2, url3, url4, url5, url6, url7 } = props.slideImages;

  const images = [
    { id: 1, url: url1, title },
    { id: 2, url: url2, title },
    { id: 3, url: url3, title },
    { id: 4, url: url4, title },
    { id: 5, url: url5, title },
    { id: 6, url: url6, title },
    { id: 7, url: url7, title },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [currentEffect, setCurrentEffect] = useState(effects[0]);
  const [prevEffect, setPrevEffect] = useState(null);
  const [animating, setAnimating] = useState(false);
  const animatingRef = useRef(animating);

  useEffect(() => {
    animatingRef.current = animating;
  }, [animating]);

  const getRandomEffect = () => {
    const randomIndex = Math.floor(Math.random() * effects.length);
    return effects[randomIndex];
  };

  const changeSlide = (indexFn, useDelay = true) => {
    if (animatingRef.current) return;

    setAnimating(true);
    setPrevIndex(currentIndex);
    setPrevEffect(currentEffect);

    const newEffect = getRandomEffect();
    setCurrentEffect(newEffect);

    const delay = useDelay ? ANIMATION_DURATION : 0;

    setTimeout(() => {
      setCurrentIndex(indexFn);
      setAnimating(false);
      setPrevIndex(null);
      setPrevEffect(null);
    }, delay);
  };

  const nextSlide = () => {
    changeSlide((prev) => (prev + 1) % images.length, false);
  };

  const prevSlide = () => {
    changeSlide((prev) => (prev - 1 + images.length) % images.length, false);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!animatingRef.current) {
        changeSlide((prev) => (prev + 1) % images.length, true);
      }
    }, AUTO_SLIDE_DELAY);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDotClick = (index) => {
    if (animating || index === currentIndex) return;
    changeSlide(() => index, false);
  };

  return (
    <div className="carousel-wrapper">
      <div className="carousel-slide-wrapper">
        {animating && prevIndex !== null && prevEffect && (
          <SlideContent
            image={images[prevIndex]}
            effect={prevEffect}
            isPrevious={true}
          />
        )}
        <SlideContent
          image={images[currentIndex]}
          effect={currentEffect}
          isPrevious={false}
          animating={animating}
        />
      </div>

      <div className="carousel-control left" onClick={prevSlide}>
        <IoIosArrowBack size={24} color="white" />
      </div>
      <div className="carousel-control right" onClick={nextSlide}>
        <IoIosArrowBack
          size={24}
          color="white"
          style={{ transform: 'rotate(180deg)' }}
        />
      </div>

      <div className="carousel-indicators">
        {images.map((img, index) => (
          <span
            key={img.id}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>
    </div>
  );
};

const SlideContent = ({ image, effect, isPrevious }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const caption = <div className="caption">{image.title}</div>;

  const getAnimationDelay = (effect, row, col) => {
    switch (effect) {
      case 'sliceTopDown':
        return row * 100;
      case 'sliceLeftRight':
        return col * 100;
      case 'sliceDiagonal':
        return (row + col) * 80;
      default:
        return (row + col) * 80;
    }
  };

  return (
    <div
      className={`grid ${isPrevious ? 'slice-prev' : 'slice-current'}`}
      key={image.id}
    >
      <LazyImage
        src={image.url}
        alt={image.title}
        className="carousel-lazy-img"
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
        onLoad={() => setImgLoaded(true)}
      />

      {imgLoaded &&
      Array(GRID_ROWS)
        .fill(0)
        .map((_, row) =>
          Array(GRID_COLS)
            .fill(0)
            .map((__, col) => (
              <div
                key={`${row}-${col}`}
                className={`tile ${effect}`}
                style={{
                  backgroundImage: `url(${image.url})`,
                  backgroundPosition: `${(col / (GRID_COLS - 1)) * 100}% ${(row / (GRID_ROWS - 1)) * 100}%`,
                  animationDelay: `${getAnimationDelay(effect, row, col)}ms`,
                }}
              />
            ))
        )}
      {caption}
    </div>
  );
};

export default AnimatedCarousel;
